import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { 
  ArrowLeft, ChevronLeft, ChevronRight, Users, Wifi, Tv, 
  UtensilsCrossed, Bath, Wind, TreePine, X, Maximize2, Home
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extended image database for each accommodation type
const accommodationImages = {
  'Apartament': [
    'https://static.wixstatic.com/media/9ecdfe_c87cdab910824a5da6746041f0c02161~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_90793d4b89b94e249f297d36f3774c83~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_ac682a24e5fe497fbcac4c16a89dc299~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_82d72d7073914b9b8f8d0095dd72e6e8~mv2.jpg',
  ],
  'Domek letniskowy': [
    'https://static.wixstatic.com/media/9ecdfe_f2484810052445cc96774efd72b9fa2e~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_a3ca21ae0a064c6abd5f7767941a92e0~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_7d76c9c4dd3c4503a3f094ba70074451~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_9c1788034f684e26bbfca9d484762aac~mv2.jpg',
  ],
  'Stodoła Duża': [
    'https://static.wixstatic.com/media/9ecdfe_6bcf62e561c344dda31e0524d2db16d5~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_0f17a613931b41d79af1fe90cb823e01~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_82f67c86874247a6a62bbbab88ab3d33~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_590f2492055c4be59909990f438895b4~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_b7a8d293394b433c837a2312f00fc11e~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_0fc7a9ad7c594781bfa14f5c8f3b1298~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_3427797a89054fb9bbbba4194f0c02f4~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_d1dce1c950ff4f969de5c4b12c9538a0~mv2.jpg',
  ],
  'Stodoła Mała': [
    'https://static.wixstatic.com/media/9ecdfe_3eacda38a74548a9bab488827ed26081~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_e54f50c709a54f8e863530d840457953~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_b719550ec8294c0bb2b2d75fa1839c49~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_36a517f50e5740cf9b98f5bb8143db07~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_62db3b4f1c654720a79a768f4c00e1ab~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_9b09c22b5fce47c9967c40b26d625a9b~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_79e62df08d3442a695351f2c70c5c18b~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_0f8eed0b91c341408d0a36a765f907a6~mv2.jpg',
  ],
  'Pokój 2-osobowy': [
    'https://static.wixstatic.com/media/9ecdfe_90793d4b89b94e249f297d36f3774c83~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_ac682a24e5fe497fbcac4c16a89dc299~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_82d72d7073914b9b8f8d0095dd72e6e8~mv2.jpg',
  ],
  'Pokój 4-osobowy': [
    'https://static.wixstatic.com/media/9ecdfe_90793d4b89b94e249f297d36f3774c83~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_c87cdab910824a5da6746041f0c02161~mv2.jpg',
    'https://static.wixstatic.com/media/9ecdfe_ac682a24e5fe497fbcac4c16a89dc299~mv2.jpg',
  ],
};

const amenityIcons = {
  'Wi-Fi': Wifi,
  'TV': Tv,
  'Aneks kuchenny': UtensilsCrossed,
  'Kuchnia': UtensilsCrossed,
  'Łazienka': Bath,
  'Klimatyzacja': Wind,
  'Widok na las': TreePine,
  'Taras': Home,
  'Antresola': Home,
};

const AccommodationDetailPage = () => {
  const { id } = useParams();
  const [accommodation, setAccommodation] = useState(null);
  const [allAccommodations, setAllAccommodations] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchAccommodation();
    fetchAllAccommodations();
  }, [id]);

  const fetchAccommodation = async () => {
    try {
      const response = await axios.get(`${API}/accommodations/${id}`);
      setAccommodation(response.data);
    } catch (error) {
      console.error('Error fetching accommodation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllAccommodations = async () => {
    try {
      const response = await axios.get(`${API}/accommodations`);
      setAllAccommodations(response.data);
    } catch (error) {
      console.error('Error fetching accommodations:', error);
    }
  };

  const getImages = () => {
    if (!accommodation) return [];
    // First check if accommodation has its own uploaded images
    if (accommodation.images && accommodation.images.length > 0) {
      return accommodation.images;
    }
    // Fallback to default images by name
    return accommodationImages[accommodation.name] || [];
  };

  const images = getImages();

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextLightboxImage = () => {
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevLightboxImage = () => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse font-heading text-2xl text-forest">Ładowanie...</div>
      </div>
    );
  }

  if (!accommodation) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-forest mb-4">Nie znaleziono obiektu</h1>
          <Link to="/">
            <Button className="bg-forest hover:bg-forest-light text-white">
              Wróć do strony głównej
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const otherAccommodations = allAccommodations.filter(a => a.id !== id);

  return (
    <div className="min-h-screen bg-sand" data-testid="accommodation-detail-page">
      {/* Header */}
      <header className="bg-white shadow-soft sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/#offer" className="flex items-center gap-2 text-forest hover:text-terracotta transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-body">Wróć do oferty</span>
          </Link>
          <h1 className="font-heading text-xl md:text-2xl text-forest">{accommodation.name}</h1>
          <a href="/#contact">
            <Button className="bg-terracotta hover:bg-terracotta-dark text-white rounded-none" data-testid="book-now-btn">
              Kontakt
            </Button>
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Main Image Gallery */}
        <section className="mb-12">
          <div className="relative">
            {/* Main Image */}
            <div className="relative h-[50vh] md:h-[60vh] overflow-hidden bg-forest/10">
              <img
                src={images[currentImageIndex]}
                alt={`${accommodation.name} - zdjęcie ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10"
                data-testid="prev-image-btn"
              >
                <ChevronLeft className="w-6 h-6 text-forest" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10"
                data-testid="next-image-btn"
              >
                <ChevronRight className="w-6 h-6 text-forest" />
              </button>

              {/* Image Counter */}
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full font-body text-sm z-10">
                {currentImageIndex + 1} / {images.length}
              </div>

              {/* Fullscreen Button */}
              <button
                onClick={() => openLightbox(currentImageIndex)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10"
                data-testid="fullscreen-btn"
              >
                <Maximize2 className="w-5 h-5 text-forest" />
              </button>
            </div>

            {/* Thumbnail Strip */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-24 h-16 md:w-32 md:h-20 overflow-hidden transition-all ${
                    index === currentImageIndex 
                      ? 'ring-2 ring-terracotta ring-offset-2' 
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  data-testid={`thumbnail-${index}`}
                >
                  <img
                    src={img}
                    alt={`Miniatura ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Details Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-8">
            {/* Description */}
            <section>
              <h2 className="font-heading text-2xl text-forest mb-4">O obiekcie</h2>
              <p className="font-body text-forest/80 leading-relaxed">
                {accommodation.description}
              </p>
            </section>

            {/* Amenities */}
            <section>
              <h2 className="font-heading text-2xl text-forest mb-4">Udogodnienia</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {accommodation.amenities?.map((amenity, index) => {
                  const IconComponent = amenityIcons[amenity] || Home;
                  return (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white rounded shadow-soft">
                      <IconComponent className="w-5 h-5 text-terracotta" />
                      <span className="font-body text-forest">{amenity}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* What's Included */}
            <section>
              <h2 className="font-heading text-2xl text-forest mb-4">W cenie pobytu</h2>
              <ul className="grid grid-cols-2 gap-2">
                {(() => {
                  // Default items if none set
                  const defaultItems = [
                    { text: 'Parking gratis', icon: 'car' },
                    { text: 'Wi-Fi', icon: 'wifi' },
                    { text: 'Pościel', icon: 'bed' },
                    { text: 'Altany ogrodowe', icon: 'tree-pine' },
                    { text: 'Grille, miejsce na ognisko', icon: 'flame' },
                    { text: 'Basen (sezon)', icon: 'waves' },
                    { text: 'Tyrolka', icon: 'cable' },
                    { text: 'Place zabaw', icon: 'baby' },
                    { text: 'Trampolina', icon: 'move' },
                    { text: 'Rowery', icon: 'bike' },
                  ];
                  
                  const items = accommodation.included_items && accommodation.included_items.length > 0 
                    ? accommodation.included_items 
                    : defaultItems;
                  
                  // Parse items - split by comma if text contains commas
                  const parsedItems = [];
                  items.forEach((item, idx) => {
                    const text = item.text || item;
                    if (text && text.includes(',')) {
                      // Split by comma and create separate items
                      text.split(',').forEach((part, partIdx) => {
                        const trimmed = part.trim();
                        if (trimmed) {
                          parsedItems.push({ id: `${item.id || idx}-${partIdx}`, text: trimmed, icon: item.icon });
                        }
                      });
                    } else if (text) {
                      parsedItems.push({ id: item.id || idx, text: text.trim(), icon: item.icon });
                    }
                  });
                  
                  return parsedItems.map((item) => (
                    <li key={item.id} className="flex items-center gap-2 font-body text-forest/80">
                      <span className="w-2 h-2 bg-terracotta rounded-full flex-shrink-0" />
                      {item.text}
                    </li>
                  ));
                })()}
              </ul>
            </section>
          </div>

          {/* Sidebar - Pricing */}
          <div className="space-y-6">
            <div className="bg-white p-6 shadow-soft sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-terracotta" />
                <span className="font-body text-forest">do {accommodation.capacity} osób</span>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <p className="font-body text-sm text-forest/60">Poza sezonem</p>
                  <p className="font-heading text-3xl text-forest">
                    {accommodation.price_low_season} zł
                    <span className="text-base font-body text-forest/60">/noc</span>
                  </p>
                </div>
                <div>
                  <p className="font-body text-sm text-forest/60">W sezonie (maj-listopad)</p>
                  <p className="font-heading text-3xl text-terracotta">
                    {accommodation.price_high_season} zł
                    <span className="text-base font-body text-forest/60">/noc</span>
                  </p>
                </div>
                {accommodation.extra_person_price > 0 && (
                  <p className="font-body text-sm text-forest/60">
                    Dodatkowa osoba: +{accommodation.extra_person_price} zł/noc
                  </p>
                )}
              </div>

              <a href="/#contact">
                <Button 
                  className="w-full bg-terracotta hover:bg-terracotta-dark text-white rounded-none h-14 font-heading tracking-wider text-lg"
                  data-testid="book-now-sidebar-btn"
                >
                  Skontaktuj się
                </Button>
              </a>

              <p className="font-body text-xs text-forest/50 text-center mt-4">
                Doba trwa od 14:00 do 10:00
              </p>
            </div>
          </div>
        </div>

        {/* Other Accommodations */}
        {otherAccommodations.length > 0 && (
          <section className="mt-16">
            <h2 className="font-heading text-2xl text-forest mb-6">Zobacz również</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {otherAccommodations.slice(0, 4).map((acc) => (
                <Link 
                  key={acc.id} 
                  to={`/accommodation/${acc.id}`}
                  className="group"
                >
                  <div className="bg-white shadow-soft overflow-hidden hover:-translate-y-1 transition-all duration-300">
                    <div className="h-40 overflow-hidden">
                      <img
                        src={accommodationImages[acc.name]?.[0] || acc.images?.[0]}
                        alt={acc.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-lg text-forest group-hover:text-terracotta transition-colors">
                        {acc.name}
                      </h3>
                      <p className="font-body text-sm text-forest/60">
                        od {acc.price_low_season} zł/noc
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-0">
          <div className="relative w-full h-[90vh] flex items-center justify-center">
            <img
              src={images[lightboxIndex]}
              alt={`${accommodation.name} - pełny widok`}
              className="max-w-full max-h-full object-contain"
            />
            
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center z-20"
            >
              <X className="w-5 h-5 text-forest" />
            </button>

            <button
              onClick={prevLightboxImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center z-20"
            >
              <ChevronLeft className="w-6 h-6 text-forest" />
            </button>
            <button
              onClick={nextLightboxImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center z-20"
            >
              <ChevronRight className="w-6 h-6 text-forest" />
            </button>

            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full font-body z-20">
              {lightboxIndex + 1} / {images.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccommodationDetailPage;
